import { Box, Flex, Text } from '@optiaxiom/react';

import { MarkdownText } from '@frontend/components/MarkdownText';
import { useOptimizelyUser } from '@frontend/hooks';

import { ExecutionStatusBadge } from './ExecutionStatusBadge';

type ExecutionDetailsCardProps = {
  executionId: string;
  status: string;
  agentId?: string;
  createdAt?: string;
  executionTime?: number;
  tokenUsage?: {
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
  };
  variant?: 'compact' | 'detailed';
  hideStatusBadge?: boolean;
};

export function ExecutionDetailsCard({
  executionId,
  status,
  agentId,
  createdAt,
  executionTime,
  tokenUsage,
  variant = 'compact',
  hideStatusBadge = false,
}: ExecutionDetailsCardProps) {
  const formatExecutionTime = (time: number): string => {
    return `${time.toFixed(2)} seconds`;
  };

  const { isOptimizelyUser } = useOptimizelyUser();

  const shouldShowTokenUsage = tokenUsage && isOptimizelyUser;

  if (variant === 'compact') {
    return (
      <Box border="1" borderColor="border.default" rounded="md">
        <Flex flexDirection="column">
          <Box bg="bg.secondary" p="12">
            <Text fontSize="sm" fontFamily="mono">
              <strong>Execution ID:</strong> {executionId}
            </Text>
            <Text fontSize="sm" fontFamily="mono">
              <strong>Status:</strong> {status}
            </Text>
            {shouldShowTokenUsage && (
              <Text fontSize="sm" fontFamily="mono">
                <strong>Tokens:</strong> {tokenUsage.total_tokens} total ({tokenUsage.input_tokens}{' '}
                input, {tokenUsage.output_tokens} output)
              </Text>
            )}
          </Box>
        </Flex>
      </Box>
    );
  }

  // Detailed variant for sidebar
  const executionTimeRow = executionTime
    ? `| **Execution Time** | ${formatExecutionTime(executionTime)} |`
    : '';

  const tokenUsageRow = shouldShowTokenUsage
    ? `| **Tokens** | ${tokenUsage.total_tokens} total (${tokenUsage.input_tokens} input, ${tokenUsage.output_tokens} output) |`
    : '';

  const agentIdRow = agentId ? `| **Agent ID** | \`${agentId}\` |` : '';

  const createdAtRow = createdAt ? `| **Created** | ${new Date(createdAt).toLocaleString()} |` : '';

  const markdownContent = `| Field | Value |
|-------|--------|
| **Execution ID** | \`${executionId}\` |
${agentIdRow}
${createdAtRow}
${executionTimeRow}
${tokenUsageRow}`
    .replace(/^\n+|\n+$/g, '')
    .replace(/\n\n+/g, '\n');

  return (
    <Box border="1" borderColor="border.default" rounded="md">
      <Box bg="bg.default" p="12">
        <Flex flexDirection="column" gap="8">
          <MarkdownText text={markdownContent} />
          {variant === 'detailed' && !hideStatusBadge && (
            <Flex flexDirection="row" alignItems="center" gap="8">
              <Text fontSize="sm" fontWeight="600">
                Status:
              </Text>
              <ExecutionStatusBadge status={status} showAsRunning />
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
